import { mergeSettings, type TenantEntry } from '@linksoft/tenant-registry';
import featureCatalogue from '../../app/assets/json/features.json';
import { NO_PRESET, PORTAL_PRESETS as presets } from '../presets';
import type { FeatureState, PageState } from '../types/customer';
import {
	defaultPresetName,
	type PortalFeatureSettings,
	type PortalSettings,
} from '../types/portal-settings';

interface CatalogueEntry {
	configKeys?: Record<string, { default?: unknown }>;
}

const CATALOGUE = featureCatalogue as unknown as Record<string, CatalogueEntry>;

/** Every feature id declared in features.json, with its configKey defaults applied. */
function catalogueDefaults(): Record<string, PortalFeatureSettings> {
	const out: Record<string, PortalFeatureSettings> = {};
	for (const [id, entry] of Object.entries(CATALOGUE)) {
		const feature: PortalFeatureSettings = { enabled: false };
		for (const [key, spec] of Object.entries(entry.configKeys ?? {})) {
			if (spec.default !== undefined) feature[key] = spec.default;
		}
		out[id] = feature;
	}
	return out;
}

function assertKnownFeatures(features: Record<string, unknown>, source: string): void {
	for (const id of Object.keys(features)) {
		if (!(id in CATALOGUE)) {
			throw new Error(
				`[config] ${source} configures unknown feature "${id}". ` +
					`Add it to layers/config/app/assets/json/features.json or fix the id. ` +
					`Known: ${Object.keys(CATALOGUE).join(', ')}.`,
			);
		}
	}
}

/**
 * Compose one deployment's portal settings.
 *
 * Merge order, lowest first:
 *   1. the feature catalogue's declared defaults
 *   2. the named preset (defaults to `<productType>-<portalType>`)
 *   3. `customer.settings`  - shared by every deployment of that customer
 *   4. `deployment.settings` - this one frontend's delta
 *
 * Objects deep-merge; arrays and primitives replace; `null` clears a key a lower
 * layer set. Three layers plus the optional customer one is the ceiling: any
 * more and "why does this tenant behave like that" stops being answerable by
 * reading the files.
 */
export function resolvePortalSettings(entry: TenantEntry): PortalSettings {
	const { customer, deployment } = entry;
	const presetName =
		deployment.preset ?? defaultPresetName(deployment.productType, deployment.portalType!);
	// 'none' is the one reserved name: catalogue defaults plus this deployment's
	// own settings, nothing in between. For a portal whose shape is genuinely
	// one-off, and for fixtures that author a fully resolved config.
	const preset = presetName === NO_PRESET ? {} : presets[presetName];
	if (!preset) {
		throw new Error(
			`[config] ${deployment.key} names preset "${presetName}", which does not exist. ` +
				`Known presets: ${[NO_PRESET, ...Object.keys(presets)].join(', ')}.`,
		);
	}

	for (const [source, layer] of [
		[`preset "${presetName}"`, preset.features],
		[`customer "${customer.id}"`, (customer.settings as PortalSettings | undefined)?.features],
		[deployment.key, (deployment.settings as PortalSettings | undefined)?.features],
	] as const) {
		if (layer) assertKnownFeatures(layer, source);
	}

	const merged = mergeSettings<PortalSettings>(
		{ pages: [], features: catalogueDefaults() },
		preset,
		customer.settings,
		deployment.settings,
	);

	return {
		...merged,
		pages: merged.pages ?? [],
		features: merged.features ?? {},
	};
}

/**
 * Project resolved settings onto the `FeatureState` / `PageState` records the
 * ~40 `useCustomerConfig()` consumers read. `components` is always empty in
 * practice; it stays in the shape because `isComponentEnabled()` reads it.
 */
export function toFeatureStates(
	features: PortalSettings['features'],
): Record<string, FeatureState> {
	const out: Record<string, FeatureState> = {};
	for (const [id, value] of Object.entries(features)) {
		if (typeof value === 'boolean') {
			out[id] = { enabled: value, components: {}, config: {} };
			continue;
		}
		const { enabled, components, ...config } = value;
		out[id] = { enabled: enabled ?? false, components: components ?? {}, config };
	}
	return out;
}

export function toPageStates(pages: string[]): Record<string, PageState> {
	const out: Record<string, PageState> = {};
	for (const id of pages) out[id] = { enabled: true };
	return out;
}
